import React, { useState, useEffect, useRef } from "react";
import { Card } from "@/components/ui/card";
import { Loader2 } from "lucide-react";
import { useMLChatbot } from "@/hooks/useMLChatbot";
import ModeSelector from "./ModeSelector";
import QuickPrompts from "./QuickPrompts";
import MessageBubble from "./MessageBubble";
import ChatInput from "@/components/chat/ChatInput";
import { useSpeechSynthesis } from "@/hooks/speech/useSpeechSynthesis";
import { cleanTextForSpeech } from "@/hooks/speech/speechUtils";

type ChatMode = 'chat' | 'debug' | 'recommend';

const ChatInterface = () => {
  const [mode, setMode] = useState<ChatMode>('chat');
  const { messages, sendMessage, isLoading, error, clearMessages, sessionId } = useMLChatbot();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [isTTSEnabled, setIsTTSEnabled] = useState(false);
  const { generateSpeech, stopSpeech, isPlaying, isSpeechSupported } = useSpeechSynthesis();
  const lastSpokenMessageRef = useRef<number>(-1);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isLoading]);

  // Auto-speak AI responses when TTS is enabled
  useEffect(() => {
    if (isTTSEnabled && messages.length > 0 && !isLoading) {
      const lastMessageIndex = messages.length - 1;
      const lastMessage = messages[lastMessageIndex];
      
      // Only speak if it's a new assistant message we haven't spoken yet
      if (
        lastMessage.role === 'assistant' && 
        lastMessageIndex > lastSpokenMessageRef.current
      ) {
        lastSpokenMessageRef.current = lastMessageIndex;
        const cleanText = cleanTextForSpeech(lastMessage.content);
        
        generateSpeech({
          text: cleanText,
          onError: (error) => {
            console.error('TTS Error:', error);
          }
        });
      }
    }
  }, [messages, isTTSEnabled, isLoading, generateSpeech]);

  const handleSendMessage = (message: string) => {
    sendMessage(message, mode);
  };

  const handleQuickPrompt = (prompt: string) => {
    sendMessage(prompt, mode);
  };

  const handleToggleTTS = () => {
    if (isTTSEnabled && isPlaying) {
      stopSpeech();
    }
    setIsTTSEnabled(!isTTSEnabled);
  };

  return (
    <Card className="w-full shadow-lg">
      <div className="border-b">
        <ModeSelector 
          mode={mode} 
          onModeChange={setMode} 
          onClearChat={clearMessages}
          isTTSEnabled={isTTSEnabled}
          onToggleTTS={handleToggleTTS}
          isSpeechSupported={isSpeechSupported}
        />
      </div>

      <div className="h-[500px] overflow-y-auto p-4 space-y-4 bg-muted/20">
        {messages.length === 0 && !error && (
          <div className="flex flex-col items-center justify-center h-full text-center px-4">
            <div className="bg-primary/10 rounded-full p-6 mb-4">
              <Loader2 className="h-12 w-12 text-primary animate-pulse" />
            </div>
            <h3 className="text-lg font-semibold mb-2">Welcome to AI Tutor</h3>
            <p className="text-muted-foreground mb-6 max-w-md">
              Ask me anything about programming, debugging, or get personalized learning recommendations
            </p>
            <QuickPrompts mode={mode} onPromptClick={handleQuickPrompt} />
          </div>
        )}

        {error && !error.includes('TensorFlow') && !error.includes('ML') && (
          <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 text-center">
            <p className="text-destructive font-medium">Error: {error}</p>
          </div>
        )}

        {messages.map((message, index) => {
          const isLastMessage = index === messages.length - 1;
          const isStreamingMessage = isLastMessage && message.role === 'assistant' && isLoading;
          
          return (
            <MessageBubble
              key={index}
              role={message.role}
              content={message.content}
              metadata={message.metadata}
              isStreaming={isStreamingMessage}
            />
          );
        })}

        <div ref={messagesEndRef} />
      </div>

      <div className="border-t p-4 space-y-3">
        {messages.length > 0 && (
          <QuickPrompts mode={mode} onPromptClick={handleQuickPrompt} compact />
        )}
        <ChatInput 
          sendMessage={handleSendMessage}
          isLoading={isLoading}
          placeholder={`Ask me anything in ${mode} mode...`}
        />
      </div>
    </Card>
  );
};

export default ChatInterface;
